import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";

import { CartProvider } from "@/components/dashboard/cart-context";

export const Route = createFileRoute("/_auth")({
  component: AuthLayout,
  beforeLoad: async ({ context }) => {
    const session = context.session;

    // If the browser is offline and we have a cached session (even stale), keep
    // the user in the app. NEVER delete the session or redirect to login just
    // because the network is unavailable. The banner component will warn them.
    const isOnline = typeof window !== "undefined" ? navigator.onLine : true;
    const isOffline = !isOnline;
    const isOfflineMarker = (session as any)?.__offline === true;

    if ((isOffline || isOfflineMarker) && session?.data) {
      return { session };
    }

    if (!session || !session.data) {
      // If this is a network error (not a genuine 401), don't redirect to login.
      // Instead, throw the error so the error boundary shows a retry UI.
      // This prevents users from being kicked out during transient network issues.
      const isNetworkError = (session as any)?.__networkError === true;
      const errorStatus = (session as any)?.error?.status;
      const isGenuineAuthError = errorStatus === 401 || errorStatus === 403;

      if (isNetworkError || (session?.error && !isGenuineAuthError)) {
        // Report the network failure to Sentry so we have visibility
        const networkErr = new Error(
          `Session fetch failed (network): ${(session as any)?.error?.message || "unknown"}`,
        );
        Sentry.captureException(networkErr, {
          level: "warning",
          tags: { type: "network_session_failure" },
        });
        // Throw as error to trigger the error boundary (retry UI) instead of
        // silently redirecting to login
        throw networkErr;
      }

      // Genuine "no session" – user is truly unauthenticated
      // If the session expired or was invalidated while impersonating, the stale
      // admin_session token can remain in localStorage and confuse subsequent
      // auth requests. Clear it before redirecting to login.
      if (typeof window !== "undefined") {
        localStorage.removeItem("admin_session");
      }
      throw redirect({
        to: "/login",
      });
    }
    // Block raw global admins from accessing tenant-scoped POS layouts
    if (session.data.user.role === "admin") {
      throw redirect({
        to: "/superadmin",
      });
    }
    return { session };
  },
});

function AuthLayout() {
  return (
    <CartProvider>
      <Outlet />
    </CartProvider>
  );
}
